import React, { useState } from "react";
import {
  Container,
  Row,
  Col,
  Card,
  CardBody,
  Nav,
  NavItem,
  NavLink,
  TabContent,
  TabPane,
  Offcanvas,
  OffcanvasHeader,
  OffcanvasBody,
} from "reactstrap";
import classnames from "classnames";
import Breadcrumbs from "../../Components/Common/Breadcrumb";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { FaBars } from "react-icons/fa";

// import settings.scss
import "./settings.scss";

// Import your settings components
import GeneralSettings from "./GeneralSettings";
import PlanSettings from "./PlanSettings";
import UsersPermissions from "./UsersPermissions";
import PaymentSettings from "./PaymentSettings";
import ShippingSettings from "./ShippingSettings";
import TaxSettings from "./TaxSettings";
import BranchSettings from "./BranchSettings";
import DomainSettings from "./DomainSettings";
import NotificationSettings from "./NotificationSettings";
import LanguageSettings from "./LanguageSettings";
import PrivacySettings from "./PrivacySettings";
import PolicySettings from "./PolicySettings";
import IntegrationSettings from "./IntegrationSettings";

const Settings: React.FC = () => {
  const [activeTab, setActiveTab] = useState("general");
  const [isSaving, setIsSaving] = useState(false);
  const navigate = useNavigate();
  const [showDrawer, setShowDrawer] = useState(false);

  const toggle = (tab: string) => {
    if (activeTab !== tab) {
      setActiveTab(tab);
      setShowDrawer(false);
    }
  };

  const handleSettingsCancel = () => {
    navigate("/");
  };

  const handleSettingsSave = async (callback: () => Promise<void>) => {
    setIsSaving(true);
    try {
      await callback();
      toast.success("Settings saved successfully");
      navigate("/");
    } catch (error) {
      toast.error("Failed to save settings");
    } finally {
      setIsSaving(false);
    }
  };

  const toggleDrawer = () => setShowDrawer(!showDrawer);

  const NavigationContent = () => (
    <Nav pills vertical className="settings-nav">
      <NavItem>
        <NavLink
          className={classnames("settings-nav-link", {
            active: activeTab === "general",
          })}
          onClick={() => toggle("general")}
        >
          <div className="d-flex align-items-center">
            <i className="bx bx-cog fs-4 me-3" />
            <div>
              <h6 className="mb-0">General Settings</h6>
              <small className="text-muted">Basic store settings</small>
            </div>
          </div>
        </NavLink>
      </NavItem>

      {/* <NavItem>
        <NavLink
          className={classnames("settings-nav-link", {
            active: activeTab === "plan",
          })}
          onClick={() => toggle("plan")}
        >
          <div className="d-flex align-items-center">
            <i className="bx bx-credit-card fs-4 me-3" />
            <div>
              <h6 className="mb-0">Plan & Billing</h6>
              <small className="text-muted">Subscription management</small>
            </div>
          </div>
        </NavLink>
      </NavItem> */}

      <NavItem>
        <NavLink
          className={classnames("settings-nav-link", {
            active: activeTab === "users",
          })}
          onClick={() => toggle("users")}
        >
          <div className="d-flex align-items-center">
            <i className="bx bx-user-circle fs-4 me-3" />
            <div>
              <h6 className="mb-0">Users & Permissions</h6>
              <small className="text-muted">Manage user access</small>
            </div>
          </div>
        </NavLink>
      </NavItem>

      <NavItem>
        <NavLink
          className={classnames("settings-nav-link", {
            active: activeTab === "payment",
          })}
          onClick={() => toggle("payment")}
        >
          <div className="d-flex align-items-center">
            <i className="bx bx-money fs-4 me-3" />
            <div>
              <h6 className="mb-0">Payment Settings</h6>
              <small className="text-muted">Payment methods</small>
            </div>
          </div>
        </NavLink>
      </NavItem>

      {/* <NavItem>
        <NavLink
          className={classnames("settings-nav-link", {
            active: activeTab === "shipping",
          })}
          onClick={() => toggle("shipping")}
        >
          <div className="d-flex align-items-center">
            <i className="bx bx-package fs-4 me-3" />
            <div>
              <h6 className="mb-0">Shipping & Delivery</h6>
              <small className="text-muted">Delivery options & rates</small>
            </div>
          </div>
        </NavLink>
      </NavItem> */}

      {/* <NavItem>
        <NavLink
          className={classnames("settings-nav-link", {
            active: activeTab === "tax",
          })}
          onClick={() => toggle("tax")}
        >
          <div className="d-flex align-items-center">
            <i className="bx bx-receipt fs-4 me-3" />
            <div>
              <h6 className="mb-0">Tax & Duty</h6>
              <small className="text-muted">Tax rates & calculations</small>
            </div>
          </div>
        </NavLink>
      </NavItem> */}

      <NavItem>
        <NavLink
          className={classnames("settings-nav-link", {
            active: activeTab === "branch",
          })}
          onClick={() => toggle("branch")}
        >
          <div className="d-flex align-items-center">
            <i className="bx bx-store fs-4 me-3" />
            <div>
              <h6 className="mb-0">Branch Settings</h6>
              <small className="text-muted">Manage store locations</small>
            </div>
          </div>
        </NavLink>
      </NavItem>

      {/* <NavItem>
        <NavLink
          className={classnames("settings-nav-link", {
            active: activeTab === "integrations",
          })}
          onClick={() => toggle("integrations")}
        >
          <div className="d-flex align-items-center">
            <i className="bx bx-plug fs-4 me-3" />
            <div>
              <h6 className="mb-0">Integrations</h6>
              <small className="text-muted">Connect services</small>
            </div>
          </div>
        </NavLink>
      </NavItem> */}

      {/* <NavItem>
        <NavLink
          className={classnames("settings-nav-link", {
            active: activeTab === "domain",
          })}
          onClick={() => toggle("domain")}
        >
          <div className="d-flex align-items-center">
            <i className="bx bx-globe fs-4 me-3" />
            <div>
              <h6 className="mb-0">Domain Settings</h6>
              <small className="text-muted">Website & SSL config</small>
            </div>
          </div>
        </NavLink>
      </NavItem> */}

      {/* <NavItem>
        <NavLink
          className={classnames("settings-nav-link", {
            active: activeTab === "notifications",
          })}
          onClick={() => toggle("notifications")}
        >
          <div className="d-flex align-items-center">
            <i className="bx bx-bell fs-4 me-3" />
            <div>
              <h6 className="mb-0">Notifications</h6>
              <small className="text-muted">Alert preferences</small>
            </div>
          </div>
        </NavLink>
      </NavItem> */}

      {/* <NavItem>
        <NavLink
          className={classnames("settings-nav-link", {
            active: activeTab === "languages",
          })}
          onClick={() => toggle("languages")}
        >
          <div className="d-flex align-items-center">
            <i className="bx bx-globe-alt fs-4 me-3" />
            <div>
              <h6 className="mb-0">Languages</h6>
              <small className="text-muted">Translation settings</small>
            </div>
          </div>
        </NavLink>
      </NavItem> */}

      {/* <NavItem>
        <NavLink
          className={classnames("settings-nav-link", {
            active: activeTab === "privacy",
          })}
          onClick={() => toggle("privacy")}
        >
          <div className="d-flex align-items-center">
            <i className="bx bx-lock-alt fs-4 me-3" />
            <div>
              <h6 className="mb-0">Customer Privacy</h6>
              <small className="text-muted">Data protection</small>
            </div>
          </div>
        </NavLink>
      </NavItem> */}

      {/* <NavItem>
        <NavLink
          className={classnames("settings-nav-link", {
            active: activeTab === "policies",
          })}
          onClick={() => toggle("policies")}
        >
          <div className="d-flex align-items-center">
            <i className="bx bx-file fs-4 me-3" />
            <div>
              <h6 className="mb-0">Policies</h6>
              <small className="text-muted">Terms & conditions</small>
            </div>
          </div>
        </NavLink>
      </NavItem> */}
    </Nav>
  );

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>
          <Breadcrumbs
            title="Settings"
            breadcrumbItem={
              activeTab.charAt(0).toUpperCase() + activeTab.slice(1)
            }
          />

          <button
            className="btn btn-primary d-lg-none"
            onClick={toggleDrawer}
            style={{
              position: "fixed",
              bottom: "20px",
              right: "20px",
              zIndex: 9999,
              borderRadius: "50%",
              width: "50px",
              height: "50px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              boxShadow: "0 2px 5px rgba(0,0,0,0.2)",
              backgroundColor: "#007f7f",
              border: "none",
            }}
          >
            <FaBars size={20} />
          </button>

          <Offcanvas
            isOpen={showDrawer}
            toggle={toggleDrawer}
            direction="start"
            className="d-lg-none"
          >
            <OffcanvasHeader toggle={toggleDrawer}>
              Settings Menu
            </OffcanvasHeader>
            <OffcanvasBody>
              <NavigationContent />
            </OffcanvasBody>
          </Offcanvas>

          <Row>
            <Col lg="3" className="d-none d-lg-block">
              <Card>
                <CardBody className="p-0">
                  <NavigationContent />
                </CardBody>
              </Card>
            </Col>

            <Col lg="9" xs="12">
              <Card>
                <CardBody>
                  <TabContent activeTab={activeTab}>
                    <TabPane tabId="general">
                      <GeneralSettings
                        onGlobalCancel={handleSettingsCancel}
                        isSaving={isSaving}
                        onSave={handleSettingsSave}
                      />
                    </TabPane>
                    <TabPane tabId="plan">
                      <PlanSettings
                        onGlobalCancel={handleSettingsCancel}
                        isSaving={isSaving}
                        onSave={handleSettingsSave}
                      />
                    </TabPane>
                    <TabPane tabId="users">
                      <UsersPermissions
                        onGlobalCancel={handleSettingsCancel}
                        isSaving={isSaving}
                        onSave={handleSettingsSave}
                      />
                    </TabPane>
                    <TabPane tabId="payment">
                      <PaymentSettings
                        onGlobalCancel={handleSettingsCancel}
                        isSaving={isSaving}
                        onSave={handleSettingsSave}
                      />
                    </TabPane>
                    <TabPane tabId="shipping">
                      <ShippingSettings
                        onGlobalCancel={handleSettingsCancel}
                        isSaving={isSaving}
                        onSave={handleSettingsSave}
                      />
                    </TabPane>
                    <TabPane tabId="tax">
                      <TaxSettings
                        onGlobalCancel={handleSettingsCancel}
                        isSaving={isSaving}
                        onSave={handleSettingsSave}
                      />
                    </TabPane>
                    <TabPane tabId="branch">
                      <BranchSettings
                        onGlobalCancel={handleSettingsCancel}
                        isSaving={isSaving}
                        onSave={handleSettingsSave}
                      />
                    </TabPane>
                    <TabPane tabId="integrations">
                      <IntegrationSettings
                        onGlobalCancel={handleSettingsCancel}
                        isSaving={isSaving}
                        onSave={handleSettingsSave}
                      />
                    </TabPane>
                    <TabPane tabId="domain">
                      <DomainSettings
                        onGlobalCancel={handleSettingsCancel}
                        isSaving={isSaving}
                        onSave={handleSettingsSave}
                      />
                    </TabPane>
                    <TabPane tabId="notifications">
                      <NotificationSettings
                        onGlobalCancel={handleSettingsCancel}
                        isSaving={isSaving}
                        onSave={handleSettingsSave}
                      />
                    </TabPane>
                    <TabPane tabId="languages">
                      <LanguageSettings
                        onGlobalCancel={handleSettingsCancel}
                        isSaving={isSaving}
                        onSave={handleSettingsSave}
                      />
                    </TabPane>
                    <TabPane tabId="privacy">
                      <PrivacySettings
                        onGlobalCancel={handleSettingsCancel}
                        isSaving={isSaving}
                        onSave={handleSettingsSave}
                      />
                    </TabPane>
                    <TabPane tabId="policies">
                      <PolicySettings
                        onGlobalCancel={handleSettingsCancel}
                        isSaving={isSaving}
                        onSave={handleSettingsSave}
                      />
                    </TabPane>
                  </TabContent>
                </CardBody>
              </Card>
            </Col>
          </Row>
        </Container>
      </div>
    </React.Fragment>
  );
};

export default Settings;
